import { Box, TextField, Typography, Tooltip, IconButton } from "@mui/material";
import { Info, AutoAwesome } from "@mui/icons-material";
import MediaPicker from "../MediaPicker";
import type { PlatformKey } from "../models";

type Props = {
  caption: string;
  onCaptionChange: (value: string) => void;
  selectedPlatforms: PlatformKey[];
  getPlatformConfig: (p: PlatformKey) => { maxLength: number; icon: (p?: any) => JSX.Element };
  mediaUrls: string[];
  setMediaUrls: (urls: string[]) => void;
  onMediaKindsChange: (info: { hasPhoto: boolean; hasVideo: boolean }) => void;
  onAiClick?: () => void;
};

export default function DraftTab({
  caption,
  onCaptionChange,
  selectedPlatforms,
  getPlatformConfig,
  mediaUrls,
  setMediaUrls,
  onMediaKindsChange,
  onAiClick
}: Props) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={styles.box}>
        <TextField
          multiline
          minRows={5}
          label="Caption (Draft)"
          placeholder="Write something to post..."
          value={caption}
          onChange={e => onCaptionChange(e.target.value)}
          fullWidth
          sx={onAiClick ? { '& .MuiInputBase-input': { pb: '32px' } } : undefined}
        />
        {onAiClick && (
          <Box sx={styles.aiButton}>
            <Tooltip title="Generate caption with AI">
              <IconButton size="small" color="secondary" onClick={onAiClick}>
                <AutoAwesome fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {selectedPlatforms.length > 0 && (
          <Box sx={styles.platforms}>
            {selectedPlatforms.map(p => {
              const cfg = getPlatformConfig(p);
              const over = caption.length > cfg.maxLength;
              return (
                <Box
                  key={p}
                  sx={{
                    ...styles.badge,
                    color: over ? "error.main" : "text.secondary",
                    borderColor: over ? "error.main" : "divider"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {cfg.icon({ fontSize: "small" })}
                    <Typography variant="caption">
                      {caption.length}/{cfg.maxLength}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", fontSize: 16, gap: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 'bold' }} variant="subtitle2">Add Media</Typography>
        <Tooltip title="You can upload up to 10 media, 2 GB each" color="text.secondary">
          <Info fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <MediaPicker
        value={mediaUrls}
        onChange={setMediaUrls}
        maxItems={10}
        onKindsChange={onMediaKindsChange}
      />
    </Box>
  );
}

const styles = {
  box: {
    position: "relative"
  },
  aiButton: {
    position: "absolute",
    bottom: 6,
    left: 8
  },
  platforms: {
    position: "absolute",
    bottom: 6,
    right: 8,
    display: "flex",
    gap: 0.75,
    flexWrap: "wrap",
    justifyContent: "flex-end"
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    border: "1px solid",
    borderRadius: 1,
    px: 0.75,
    py: 0.25,
    bgcolor: "background.paper"
  }
};
